import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchSearchConsoleQuery,
  fetchSearchConsoleSitemaps,
  fetchPageSpeedBatch,
  saveClientGscSiteUrl,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Search, Globe, Calendar, AlertCircle, Loader2, TrendingUp, TrendingDown, Minus,
  Eye, MousePointerClick, Target, Activity, FileText, Zap, ShieldCheck, ShieldAlert,
  ArrowUp, ArrowDown, ArrowUpDown, FileX, FileCheck, Layers,
} from "lucide-react";
import { toast } from "sonner";

/* ───────────────────────── period utilities ───────────────────────── */

const PRESETS = [
  { key: "7d",  label: "Últimos 7 días",   start: -7 },
  { key: "28d", label: "Últimos 28 días",  start: -28 },
  { key: "90d", label: "Últimos 90 días",  start: -90 },
  { key: "ytd", label: "Año actual",       start: "ytd" },
  { key: "1y",  label: "Últimos 12 meses", start: -365 },
];
const MONTH_NAMES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
function buildMonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({
      key: `m-${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`,
      label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
      year: d.getFullYear(), month: d.getMonth(),
    });
  }
  return opts;
}
const MONTH_OPTIONS = buildMonthOptions();

const iso = (d) => d.toISOString().slice(0, 10);
function rangeFromPreset(key, customFrom, customTo) {
  if (key === "custom") return { start_date: customFrom, end_date: customTo };
  const end = new Date();
  if (key === "ytd") return { start_date: iso(new Date(end.getFullYear(),0,1)), end_date: iso(end) };
  if (key?.startsWith?.("m-")) {
    const opt = MONTH_OPTIONS.find((o) => o.key === key);
    if (opt) {
      const today = new Date();
      const start = new Date(opt.year, opt.month, 1);
      const last = new Date(opt.year, opt.month + 1, 0);
      return { start_date: iso(start), end_date: iso(last > today ? today : last) };
    }
  }
  const p = PRESETS.find((x) => x.key === key) || PRESETS[1];
  const start = new Date();
  start.setDate(start.getDate() + p.start);
  return { start_date: iso(start), end_date: iso(end) };
}

/** Returns the equivalent previous-period range (for trend comparison). */
function previousRange(range) {
  const s = new Date(range.start_date);
  const e = new Date(range.end_date);
  const days = Math.round((e - s) / 86400000) + 1;
  const prevEnd = new Date(s);
  prevEnd.setDate(prevEnd.getDate() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - (days - 1));
  return { start_date: iso(prevStart), end_date: iso(prevEnd) };
}

/* ───────────────────────── formatters ───────────────────────── */

const fmtNum = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return `${(v/1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v/1e3).toFixed(1)}k`;
  return v.toLocaleString("es-AR", { maximumFractionDigits: 0 });
};
const fmtPct = (n) => `${(Number(n || 0) * 100).toFixed(2)}%`;
const fmtPos = (n) => (n == null ? "—" : Number(n).toFixed(1));
const fmtDelta = (curr, prev) => {
  if (prev == null || prev === 0) return null;
  return ((curr - prev) / prev) * 100;
};

function TrendBadge({ delta, invertColor = false }) {
  if (delta == null || !isFinite(delta)) return <span className="text-slate-300 text-[10px]">—</span>;
  const isUp = delta > 0;
  const isFlat = Math.abs(delta) < 1;
  const goodIsUp = !invertColor;
  const color = isFlat
    ? "text-slate-500 bg-slate-100"
    : (isUp === goodIsUp ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50");
  const Icon = isFlat ? Minus : (isUp ? ArrowUp : ArrowDown);
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${color}`}>
      <Icon className="h-2.5 w-2.5" />
      {Math.abs(delta).toFixed(1)}%
    </span>
  );
}

/* ───────────────────────── main component ───────────────────────── */

export default function SearchConsoleSection({ clientId, gscSiteUrl: initialSiteUrl = "" }) {
  const qc = useQueryClient();
  const [siteUrl, setSiteUrl] = useState(initialSiteUrl);
  const [committedSiteUrl, setCommittedSiteUrl] = useState(initialSiteUrl);
  const [editingSite, setEditingSite] = useState(!initialSiteUrl);
  const [preset, setPreset] = useState("28d");
  const todayIso = iso(new Date());
  const monthAgoIso = (() => { const d = new Date(); d.setDate(d.getDate() - 28); return iso(d); })();
  const [customFrom, setCustomFrom] = useState(monthAgoIso);
  const [customTo, setCustomTo] = useState(todayIso);

  const range = useMemo(() => rangeFromPreset(preset, customFrom, customTo), [preset, customFrom, customTo]);
  const prevRange = useMemo(() => previousRange(range), [range]);

  const saveSiteUrlMut = useMutation({
    mutationFn: (url) => saveClientGscSiteUrl(clientId, url),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cro", clientId] }),
  });

  const handleApplySiteUrl = () => {
    const v = siteUrl.trim();
    if (!v) return;
    setCommittedSiteUrl(v);
    setEditingSite(false);
    saveSiteUrlMut.mutate(v); // persist in cro_data
  };

  const periodSelect = (
    <Select value={preset} onValueChange={setPreset}>
      <SelectTrigger className="h-9 text-sm w-56" data-testid="gsc-period">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-[420px]">
        <div className="px-2 py-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">Atajos</div>
        {PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
        <div className="px-2 pt-2 py-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">Por mes</div>
        {MONTH_OPTIONS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
        <div className="border-t border-slate-100 mt-1" />
        <SelectItem value="custom">Rango personalizado…</SelectItem>
      </SelectContent>
    </Select>
  );

  if (!committedSiteUrl || editingSite) {
    return (
      <div className="space-y-5" data-testid="search-console-section">
        <SiteUrlPrompt
          siteUrl={siteUrl}
          setSiteUrl={setSiteUrl}
          onApply={handleApplySiteUrl}
          existing={!!committedSiteUrl}
          onCancel={() => { setSiteUrl(committedSiteUrl); setEditingSite(false); }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="search-console-section">
      <SiteUrlHeader
        siteUrl={committedSiteUrl}
        onEdit={() => setEditingSite(true)}
        periodSelect={periodSelect}
        customRow={
          preset === "custom" && (
            <CustomRangeRow from={customFrom} to={customTo} setFrom={setCustomFrom} setTo={setCustomTo} />
          )
        }
      />

      <VisibilityOverview siteUrl={committedSiteUrl} range={range} prevRange={prevRange} />
      <QueriesPanel siteUrl={committedSiteUrl} range={range} />
      <PagesPanel siteUrl={committedSiteUrl} range={range} prevRange={prevRange} />
      <IndexationPanel siteUrl={committedSiteUrl} />
      <CoreWebVitalsPanel siteUrl={committedSiteUrl} range={range} />
    </div>
  );
}

/* ───────────────────────── Site URL UI ───────────────────────── */

function SiteUrlPrompt({ siteUrl, setSiteUrl, onApply, existing, onCancel }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5">
      <div className="flex items-center gap-2 mb-1">
        <Search className="h-4 w-4 text-indigo-600" />
        <h3 className="font-display text-sm font-semibold text-slate-900">Search Console</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Ingresá el site URL de Search Console para este cliente. Lo guardo y se carga automáticamente la próxima vez.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_100px] gap-3 items-end max-w-2xl">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1">
            <Globe className="h-3 w-3" /> Site URL
          </label>
          <Input
            value={siteUrl}
            onChange={(e) => setSiteUrl(e.target.value)}
            placeholder="sc-domain:tudominio.com o https://tudominio.com/"
            className="h-9 text-sm font-mono"
            data-testid="gsc-site-url-input"
          />
        </div>
        <Button onClick={onApply} disabled={!siteUrl.trim()} className="h-9" data-testid="gsc-site-url-apply">
          Guardar y cargar
        </Button>
        {existing && (
          <Button variant="outline" onClick={onCancel} className="h-9">Cancelar</Button>
        )}
      </div>
      <p className="text-[10px] text-slate-400 mt-3 leading-relaxed">
        Formatos válidos: <span className="font-mono">sc-domain:tudominio.com</span> (Domain property, recomendado)
        o <span className="font-mono">https://tudominio.com/</span> (URL-prefix property, slash final obligatorio).
        El email <span className="font-mono">narwhal-dashboard@dashboards-emergent.iam.gserviceaccount.com</span> debe estar
        agregado como <span className="font-medium">Restricted</span> o <span className="font-medium">Full user</span> en Search Console.
      </p>
    </section>
  );
}

function SiteUrlHeader({ siteUrl, onEdit, periodSelect, customRow }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 text-xs min-w-0">
          <Globe className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <span className="text-slate-500 shrink-0">Site:</span>
          <span className="font-mono font-semibold text-slate-900 truncate">{siteUrl}</span>
          <button onClick={onEdit} className="text-indigo-600 hover:underline ml-1 shrink-0">cambiar</button>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {periodSelect}
        </div>
      </div>
      {customRow}
    </section>
  );
}

function CustomRangeRow({ from, to, setFrom, setTo }) {
  return (
    <div className="flex flex-wrap items-end gap-2 mt-2 pl-1 border-l-2 border-indigo-300" data-testid="gsc-custom-range-row">
      <div>
        <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold block">Desde</label>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 text-xs w-40" data-testid="gsc-custom-from" />
      </div>
      <div>
        <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold block">Hasta</label>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 text-xs w-40" data-testid="gsc-custom-to" />
      </div>
    </div>
  );
}

/* ───────────────────────── 1) Visibility overview ───────────────────────── */

function VisibilityOverview({ siteUrl, range, prevRange }) {
  const summaryQ = useQuery({
    queryKey: ["gsc-summary", siteUrl, range],
    queryFn: () => fetchSearchConsoleQuery({ site_url: siteUrl, ...range, dimensions: [], row_limit: 1 }),
    staleTime: 5 * 60_000,
  });
  const prevQ = useQuery({
    queryKey: ["gsc-summary-prev", siteUrl, prevRange],
    queryFn: () => fetchSearchConsoleQuery({ site_url: siteUrl, ...prevRange, dimensions: [], row_limit: 1 }),
    staleTime: 5 * 60_000,
  });
  const trendQ = useQuery({
    queryKey: ["gsc-trend", siteUrl, range],
    queryFn: () => fetchSearchConsoleQuery({ site_url: siteUrl, ...range, dimensions: ["date"], row_limit: 1000 }),
    staleTime: 5 * 60_000,
  });

  const curr = summaryQ.data?.rows?.[0] || {};
  const prev = prevQ.data?.rows?.[0] || {};
  const trend = (trendQ.data?.rows || []).map((r) => ({
    date: r.keys?.[0],
    clicks: r.clicks || 0,
    impressions: r.impressions || 0,
  }));

  const error = summaryQ.error || prevQ.error || trendQ.error;
  const loading = summaryQ.isLoading || trendQ.isLoading;

  if (error) {
    return (
      <section className="bg-rose-50 border border-rose-200 rounded-md p-4 text-sm text-rose-800 flex items-start gap-2" data-testid="gsc-overview-error">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">No se pudo cargar Search Console</div>
          <div className="text-xs mt-1">{error?.response?.data?.detail || error?.message}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 space-y-5" data-testid="gsc-overview">
      <div className="flex items-center gap-2">
        <Activity className="h-4 w-4 text-indigo-600" />
        <h3 className="font-display text-sm font-semibold text-slate-900">Visibilidad general</h3>
        <span className="text-[10px] text-slate-400 ml-1">{range.start_date} → {range.end_date}</span>
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-600 ml-auto" />}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={Eye} label="Impresiones" value={fmtNum(curr.impressions)} delta={fmtDelta(curr.impressions, prev.impressions)} tone="indigo" />
        <KpiCard icon={MousePointerClick} label="Clicks" value={fmtNum(curr.clicks)} delta={fmtDelta(curr.clicks, prev.clicks)} tone="sky" />
        <KpiCard icon={Target} label="CTR promedio" value={fmtPct(curr.ctr)} delta={fmtDelta(curr.ctr, prev.ctr)} tone="emerald" />
        <KpiCard icon={TrendingUp} label="Posición promedio" value={fmtPos(curr.position)} delta={fmtDelta(curr.position, prev.position)} invertColor tone="violet" />
      </div>

      <div className="h-48 -mx-2">
        {trend.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#94a3b8" }} tickFormatter={(d) => d?.slice(5)} interval="preserveStartEnd" />
              <YAxis yAxisId="l" tick={{ fontSize: 9, fill: "#94a3b8" }} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 9, fill: "#94a3b8" }} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4 }} />
              <Line yAxisId="l" type="monotone" dataKey="clicks" stroke="#0ea5e9" strokeWidth={2} dot={false} name="Clicks" />
              <Line yAxisId="r" type="monotone" dataKey="impressions" stroke="#6366f1" strokeWidth={2} dot={false} name="Impresiones" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-xs text-slate-400">Sin datos diarios en el período.</div>
        )}
      </div>
    </section>
  );
}

function KpiCard({ icon: Icon, label, value, delta, invertColor, tone = "slate" }) {
  const tones = {
    indigo: "from-indigo-50 to-white border-indigo-200 text-indigo-700",
    sky: "from-sky-50 to-white border-sky-200 text-sky-700",
    emerald: "from-emerald-50 to-white border-emerald-200 text-emerald-700",
    violet: "from-violet-50 to-white border-violet-200 text-violet-700",
    slate: "from-slate-50 to-white border-slate-200 text-slate-700",
  };
  return (
    <div className={`bg-gradient-to-b ${tones[tone]} border rounded-md p-3`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-3.5 w-3.5" />
        <div className="text-[9px] uppercase tracking-wider opacity-90 font-semibold">{label}</div>
      </div>
      <div className="flex items-end gap-2">
        <div className="font-mono text-2xl font-semibold tabular-nums text-slate-900">{value}</div>
        <div className="mb-1"><TrendBadge delta={delta} invertColor={invertColor} /></div>
      </div>
    </div>
  );
}

/* ───────────────────────── 2) Queries panel ───────────────────────── */

const SORT_DIRS = { asc: "asc", desc: "desc" };

function QueriesPanel({ siteUrl, range }) {
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState("clicks");
  const [sortDir, setSortDir] = useState("desc");

  const q = useQuery({
    queryKey: ["gsc-queries", siteUrl, range],
    queryFn: () => fetchSearchConsoleQuery({ site_url: siteUrl, ...range, dimensions: ["query"], row_limit: 5000 }),
    staleTime: 5 * 60_000,
  });

  const all = (q.data?.rows || []).map((r) => ({
    query: r.keys?.[0] || "",
    clicks: r.clicks || 0,
    impressions: r.impressions || 0,
    ctr: r.ctr || 0,
    position: r.position || 0,
  }));

  const filtered = useMemo(() => {
    const fn = (a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      const cmp = sortKey === "query" ? String(av).localeCompare(String(bv)) : (av - bv);
      return sortDir === "asc" ? cmp : -cmp;
    };
    return all
      .filter((r) => !filter.trim() || r.query.toLowerCase().includes(filter.toLowerCase()))
      .sort(fn);
  }, [all, filter, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "position" ? "asc" : "desc"); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="gsc-queries-panel">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
        <Search className="h-4 w-4 text-indigo-600 shrink-0" />
        <h3 className="font-display text-sm font-semibold text-slate-900">Queries</h3>
        <span className="text-[10px] text-slate-400">{all.length} totales · mostrando {filtered.length}</span>
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input placeholder="Filtrar por término…" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 pl-9 bg-white text-xs" data-testid="gsc-queries-filter" />
        </div>
      </div>

      {q.isLoading ? (
        <div className="h-40 m-3 rounded-md skeleton" />
      ) : all.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-400">Sin queries en el período.</div>
      ) : (
        <div className="overflow-x-auto max-h-[32rem] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                <SortableTh label="Query" k="query" curr={sortKey} dir={sortDir} onClick={toggleSort} className="px-5 py-2" />
                <SortableTh label="Clicks" k="clicks" curr={sortKey} dir={sortDir} onClick={toggleSort} className="px-2 py-2 text-right" />
                <SortableTh label="Impresiones" k="impressions" curr={sortKey} dir={sortDir} onClick={toggleSort} className="px-2 py-2 text-right" />
                <SortableTh label="CTR" k="ctr" curr={sortKey} dir={sortDir} onClick={toggleSort} className="px-2 py-2 text-right" />
                <SortableTh label="Posición" k="position" curr={sortKey} dir={sortDir} onClick={toggleSort} className="px-5 py-2 text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <tr key={r.query} className="hover:bg-slate-50">
                  <td className="px-5 py-2 font-medium text-slate-800">{r.query}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-sky-700 font-semibold">{fmtNum(r.clicks)}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-700">{fmtNum(r.impressions)}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{fmtPct(r.ctr)}</td>
                  <td className="px-5 py-2 text-right font-mono tabular-nums font-semibold text-indigo-700">{fmtPos(r.position)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SortableTh({ label, k, curr, dir, onClick, className }) {
  const active = curr === k;
  const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th className={className}>
      <button onClick={() => onClick(k)} className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wider text-[10px] hover:text-slate-700 ${active ? "text-indigo-700" : "text-slate-500"}`} data-testid={`gsc-sort-${k}`}>
        {label}
        <Icon className="h-2.5 w-2.5" />
      </button>
    </th>
  );
}

/* ───────────────────────── 3) Pages panel ───────────────────────── */

function PagesPanel({ siteUrl, range, prevRange }) {
  const [filter, setFilter] = useState("");
  const [trendFilter, setTrendFilter] = useState("all"); // all | up | flat | down

  const currQ = useQuery({
    queryKey: ["gsc-pages-curr", siteUrl, range],
    queryFn: () => fetchSearchConsoleQuery({ site_url: siteUrl, ...range, dimensions: ["page"], row_limit: 1000 }),
    staleTime: 5 * 60_000,
  });
  const prevQ = useQuery({
    queryKey: ["gsc-pages-prev", siteUrl, prevRange],
    queryFn: () => fetchSearchConsoleQuery({ site_url: siteUrl, ...prevRange, dimensions: ["page"], row_limit: 1000 }),
    staleTime: 5 * 60_000,
  });

  const merged = useMemo(() => {
    const curr = currQ.data?.rows || [];
    const prev = prevQ.data?.rows || [];
    const prevMap = new Map(prev.map((r) => [r.keys?.[0], r]));
    return curr.map((r) => {
      const url = r.keys?.[0] || "";
      const prevRow = prevMap.get(url) || {};
      const clicksDelta = fmtDelta(r.clicks || 0, prevRow.clicks || 0);
      let trend = "flat";
      if (clicksDelta == null) trend = "new";
      else if (clicksDelta > 10) trend = "up";
      else if (clicksDelta < -10) trend = "down";
      return {
        url,
        clicks: r.clicks || 0,
        impressions: r.impressions || 0,
        ctr: r.ctr || 0,
        position: r.position || 0,
        clicks_delta: clicksDelta,
        impressions_delta: fmtDelta(r.impressions || 0, prevRow.impressions || 0),
        trend,
      };
    });
  }, [currQ.data, prevQ.data]);

  const filtered = merged
    .filter((p) => !filter.trim() || p.url.toLowerCase().includes(filter.toLowerCase()))
    .filter((p) => trendFilter === "all" || p.trend === trendFilter)
    .sort((a, b) => b.clicks - a.clicks);

  const counts = useMemo(() => {
    const c = { up: 0, flat: 0, down: 0, new: 0 };
    for (const p of merged) c[p.trend]++;
    return c;
  }, [merged]);

  const error = currQ.error || prevQ.error;

  return (
    <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="gsc-pages-panel">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
        <FileText className="h-4 w-4 text-indigo-600 shrink-0" />
        <h3 className="font-display text-sm font-semibold text-slate-900">Páginas</h3>
        <span className="text-[10px] text-slate-400">{merged.length} totales</span>
        <div className="flex items-center gap-1.5 ml-2 text-[10px]">
          <TrendChip label="Al alza" count={counts.up} icon={TrendingUp} tone="emerald" active={trendFilter==="up"} onClick={() => setTrendFilter(trendFilter==="up"?"all":"up")} />
          <TrendChip label="Estable" count={counts.flat} icon={Minus} tone="slate" active={trendFilter==="flat"} onClick={() => setTrendFilter(trendFilter==="flat"?"all":"flat")} />
          <TrendChip label="A la baja" count={counts.down} icon={TrendingDown} tone="rose" active={trendFilter==="down"} onClick={() => setTrendFilter(trendFilter==="down"?"all":"down")} />
        </div>
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input placeholder="Filtrar por URL…" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 pl-9 bg-white text-xs" data-testid="gsc-pages-filter" />
        </div>
      </div>

      {error ? (
        <div className="px-5 py-4 text-xs text-rose-700">{error?.response?.data?.detail || error?.message}</div>
      ) : currQ.isLoading || prevQ.isLoading ? (
        <div className="h-40 m-3 rounded-md skeleton" />
      ) : merged.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-400">Sin páginas en el período.</div>
      ) : (
        <div className="overflow-x-auto max-h-[32rem] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                <th className="px-5 py-2 font-semibold">URL</th>
                <th className="px-2 py-2 font-semibold text-center">Tendencia</th>
                <th className="px-2 py-2 font-semibold text-right">Clicks</th>
                <th className="px-2 py-2 font-semibold text-right">Δ clicks</th>
                <th className="px-2 py-2 font-semibold text-right">Impresiones</th>
                <th className="px-2 py-2 font-semibold text-right">Δ impr.</th>
                <th className="px-2 py-2 font-semibold text-right">CTR</th>
                <th className="px-5 py-2 font-semibold text-right">Posición</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((p) => (
                <tr key={p.url} className="hover:bg-slate-50">
                  <td className="px-5 py-2 max-w-md truncate" title={p.url}>
                    <a href={p.url} target="_blank" rel="noreferrer" className="font-medium text-slate-800 hover:text-indigo-700 hover:underline">{p.url}</a>
                  </td>
                  <td className="px-2 py-2 text-center"><TrendCell trend={p.trend} /></td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-sky-700 font-semibold">{fmtNum(p.clicks)}</td>
                  <td className="px-2 py-2 text-right"><TrendBadge delta={p.clicks_delta} /></td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-700">{fmtNum(p.impressions)}</td>
                  <td className="px-2 py-2 text-right"><TrendBadge delta={p.impressions_delta} /></td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{fmtPct(p.ctr)}</td>
                  <td className="px-5 py-2 text-right font-mono tabular-nums font-semibold text-indigo-700">{fmtPos(p.position)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function TrendChip({ label, count, icon: Icon, tone, active, onClick }) {
  const tones = {
    emerald: active ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
    rose: active ? "bg-rose-600 text-white" : "bg-rose-50 text-rose-700 hover:bg-rose-100",
    slate: active ? "bg-slate-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200",
  };
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold transition ${tones[tone]}`}>
      <Icon className="h-2.5 w-2.5" />{label} {count}
    </button>
  );
}

function TrendCell({ trend }) {
  if (trend === "up") return <span className="inline-flex items-center gap-1 text-emerald-700"><TrendingUp className="h-3.5 w-3.5" /></span>;
  if (trend === "down") return <span className="inline-flex items-center gap-1 text-rose-700"><TrendingDown className="h-3.5 w-3.5" /></span>;
  if (trend === "new") return <span className="text-[9px] font-mono uppercase tracking-wider bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">Nueva</span>;
  return <Minus className="h-3.5 w-3.5 inline text-slate-400" />;
}

/* ───────────────────────── 4) Indexation panel ───────────────────────── */

function IndexationPanel({ siteUrl }) {
  const q = useQuery({
    queryKey: ["gsc-sitemaps", siteUrl],
    queryFn: () => fetchSearchConsoleSitemaps(siteUrl),
    staleTime: 10 * 60_000,
  });
  const sitemaps = q.data?.sitemaps || [];

  // Aggregate counts from each sitemap (contents → submitted/indexed)
  const agg = useMemo(() => {
    let submitted = 0, indexed = 0, warnings = 0, errors = 0;
    for (const s of sitemaps) {
      for (const c of (s.contents || [])) {
        submitted += Number(c.submitted || 0);
        indexed += Number(c.indexed || 0);
      }
      warnings += Number(s.warnings || 0);
      errors += Number(s.errors || 0);
    }
    return { submitted, indexed, warnings, errors, not_indexed: Math.max(0, submitted - indexed) };
  }, [sitemaps]);

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="gsc-indexation-panel">
      <div className="flex items-center gap-2">
        <Layers className="h-4 w-4 text-indigo-600" />
        <h3 className="font-display text-sm font-semibold text-slate-900">Indexación</h3>
        {q.isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-600 ml-auto" />}
      </div>

      {q.error ? (
        <div className="text-xs text-rose-700">{q.error?.response?.data?.detail || q.error?.message}</div>
      ) : sitemaps.length === 0 ? (
        <div className="text-xs text-slate-400 italic">Esta propiedad no tiene sitemaps registrados en Search Console.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard icon={FileCheck} label="URLs enviadas" value={fmtNum(agg.submitted)} tone="indigo" />
            <KpiCard icon={ShieldCheck} label="URLs indexadas" value={fmtNum(agg.indexed)} tone="emerald" />
            <KpiCard icon={FileX} label="No indexadas" value={fmtNum(agg.not_indexed)} tone="violet" />
            <KpiCard icon={ShieldAlert} label="Errores / Warnings" value={fmtNum(agg.errors + agg.warnings)} tone={(agg.errors + agg.warnings) > 0 ? "violet" : "slate"} />
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                  <th className="px-3 py-2 font-semibold">Sitemap</th>
                  <th className="px-2 py-2 font-semibold text-right">Enviadas</th>
                  <th className="px-2 py-2 font-semibold text-right">Indexadas</th>
                  <th className="px-2 py-2 font-semibold text-right">Pendientes</th>
                  <th className="px-2 py-2 font-semibold text-right">Errores</th>
                  <th className="px-3 py-2 font-semibold">Última lectura</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sitemaps.map((s) => {
                  const sub = (s.contents || []).reduce((a, c) => a + Number(c.submitted || 0), 0);
                  const idx = (s.contents || []).reduce((a, c) => a + Number(c.indexed || 0), 0);
                  return (
                    <tr key={s.path}>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-700 break-all">{s.path}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums">{fmtNum(sub)}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-emerald-700 font-semibold">{fmtNum(idx)}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-violet-700">{fmtNum(Math.max(0, sub - idx))}</td>
                      <td className={`px-2 py-2 text-right font-mono tabular-nums ${(s.errors > 0) ? "text-rose-700 font-semibold" : "text-slate-400"}`}>{fmtNum(s.errors || 0)}</td>
                      <td className="px-3 py-2 text-[10px] text-slate-500">{s.lastDownloaded ? new Date(s.lastDownloaded).toLocaleDateString("es-AR") : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="text-[10px] text-slate-400 leading-relaxed">
            "No indexadas" = URLs enviadas en sitemap menos las efectivamente indexadas. Para diagnosticar URL por URL (descubiertas pero no indexadas, errores de cobertura, etc.) usá la <span className="font-medium">URL Inspection</span> de Search Console directamente — la API tiene una cuota de 2000 inspecciones/día, costosa para listas grandes.
          </p>
        </>
      )}
    </section>
  );
}

/* ───────────────────────── 5) Core Web Vitals panel ───────────────────────── */

function CoreWebVitalsPanel({ siteUrl, range }) {
  const [strategy, setStrategy] = useState("mobile");
  const [analysis, setAnalysis] = useState(null); // {results: [...]}
  // Pick the top 5 pages by clicks from the current period
  const pagesQ = useQuery({
    queryKey: ["gsc-pages-cwv", siteUrl, range],
    queryFn: () => fetchSearchConsoleQuery({ site_url: siteUrl, ...range, dimensions: ["page"], row_limit: 10 }),
    staleTime: 5 * 60_000,
  });

  const topUrls = (pagesQ.data?.rows || []).map((r) => r.keys?.[0]).filter(Boolean).slice(0, 5);

  const mut = useMutation({
    mutationFn: () => fetchPageSpeedBatch({ urls: topUrls, strategy }),
    onSuccess: (data) => setAnalysis(data),
    onError: (err) => toast.error(err?.response?.data?.detail || "Error en PageSpeed"),
  });

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="gsc-cwv-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Zap className="h-4 w-4 text-indigo-600" />
        <h3 className="font-display text-sm font-semibold text-slate-900">Core Web Vitals</h3>
        <span className="text-[10px] text-slate-400">Top {topUrls.length} páginas · datos de campo (Chrome UX Report)</span>
        <div className="ml-auto flex items-center gap-2">
          <Select value={strategy} onValueChange={setStrategy}>
            <SelectTrigger className="h-9 text-xs w-32" data-testid="cwv-strategy"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mobile">Mobile</SelectItem>
              <SelectItem value="desktop">Desktop</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || topUrls.length === 0} className="h-9 text-xs" data-testid="cwv-run-btn">
            {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Zap className="h-3.5 w-3.5 mr-1.5" />}
            Analizar Top 5
          </Button>
        </div>
      </div>

      {!analysis ? (
        <div className="text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-md p-4">
          Clickeá <span className="font-semibold">"Analizar Top 5"</span> para correr PageSpeed Insights sobre las 5 páginas con más clicks del período. Tarda ~30-60s.
          <br /><span className="text-[10px] text-slate-400 italic">Sin API key configurada — limitado a ~1 req/seg. Para batches grandes agregá <span className="font-mono">GOOGLE_PAGESPEED_API_KEY</span>.</span>
        </div>
      ) : (
        <div className="space-y-3">
          {(analysis.results || []).map((r, i) => <CwvRow key={`${r.url}-${i}`} r={r} />)}
        </div>
      )}
    </section>
  );
}

function CwvRow({ r }) {
  if (r.error) {
    return (
      <div className="border border-rose-200 bg-rose-50 rounded-md p-3 text-xs">
        <div className="font-mono text-slate-700 truncate mb-1">{r.url}</div>
        <div className="text-rose-700">{r.error}</div>
      </div>
    );
  }
  const lcp = r.field_data?.lcp;
  const inp = r.field_data?.inp;
  const cls = r.field_data?.cls;
  const overallTone =
    r.overall_category === "FAST" ? "emerald" :
    r.overall_category === "AVERAGE" ? "amber" :
    r.overall_category === "SLOW" ? "rose" : "slate";

  return (
    <div className="border border-slate-200 rounded-md p-3">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <a href={r.url} target="_blank" rel="noreferrer" className="font-mono text-[11px] text-slate-700 hover:text-indigo-700 truncate flex-1">{r.url}</a>
        {r.overall_category && <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${overallTone === "emerald" ? "bg-emerald-100 text-emerald-700" : overallTone === "amber" ? "bg-amber-100 text-amber-700" : overallTone === "rose" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{r.overall_category}</span>}
        {r.lab_score != null && <span className="text-[10px] font-mono text-slate-500">Lab: <span className="font-semibold text-slate-800">{r.lab_score}/100</span></span>}
      </div>
      {!r.has_field_data ? (
        <div className="text-[11px] text-slate-400 italic">Sin datos de campo (CrUX) suficientes para esta URL.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <CwvMetric label="LCP" value={lcp?.percentile != null ? `${Math.round(lcp.percentile)} ms` : "—"} rating={lcp?.rating} hint="Largest Contentful Paint · objetivo <2.5s" />
          <CwvMetric label="INP" value={inp?.percentile != null ? `${Math.round(inp.percentile)} ms` : "—"} rating={inp?.rating} hint="Interaction to Next Paint · objetivo <200ms" />
          <CwvMetric label="CLS" value={cls?.percentile != null ? cls.percentile.toFixed(3) : "—"} rating={cls?.rating} hint="Cumulative Layout Shift · objetivo <0.1" />
        </div>
      )}
    </div>
  );
}

function CwvMetric({ label, value, rating, hint }) {
  const tones = {
    good: "border-emerald-300 bg-emerald-50",
    needs_improvement: "border-amber-300 bg-amber-50",
    poor: "border-rose-300 bg-rose-50",
    unknown: "border-slate-200 bg-slate-50",
  };
  const dotTones = {
    good: "bg-emerald-500",
    needs_improvement: "bg-amber-500",
    poor: "bg-rose-500",
    unknown: "bg-slate-300",
  };
  const labels = {
    good: "Bueno",
    needs_improvement: "Necesita mejora",
    poor: "Deficiente",
    unknown: "Sin datos",
  };
  return (
    <div className={`border rounded-md p-2.5 ${tones[rating] || tones.unknown}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600">{label}</div>
        <div className="flex items-center gap-1">
          <span className={`h-1.5 w-1.5 rounded-full ${dotTones[rating] || dotTones.unknown}`} />
          <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-600">{labels[rating] || labels.unknown}</span>
        </div>
      </div>
      <div className="font-mono text-xl font-semibold tabular-nums text-slate-900">{value}</div>
      <div className="text-[9px] text-slate-500 mt-0.5">{hint}</div>
    </div>
  );
}
